// ------------------------------------------------------------------------------
// name: useDebounceQuery
// author: mudas
// created: 2025/10/22
// ------------------------------------------------------------------------------

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { debounce, DebounceSettings } from 'lodash-es';
import { useCreation } from 'ahooks';

import { QueryKey } from '@tanstack/query-core';
import { UseQueryOptions } from '@tanstack/react-query';

export interface DebounceQueryOptions {
  debounceWait?: number;
  debounceLeading?: boolean;
  debounceTrailing?: boolean;
  debounceMaxWait?: number;
}

export interface DataOptions {
  queryHash?: string;
  enabled?: boolean;
}

/**
 * 让 useQuery 支持防抖查询
 * @see https://www.lodashjs.com/docs/lodash.debounce
 */
export const useDebounceQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: DataOptions & DebounceQueryOptions,
) => {
  const {
    debounceWait,
    debounceLeading,
    debounceTrailing,
    debounceMaxWait,

    ...otherOptions
  } = options;

  const [queryOptions, setQueryOptions] = useState<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>({});

  const debounceOptions = useCreation(() => {
    const ret: DebounceSettings = {};
    if (debounceLeading !== undefined) {
      ret.leading = debounceLeading;
    }
    if (debounceTrailing !== undefined) {
      ret.trailing = debounceTrailing;
    }
    if (debounceMaxWait !== undefined) {
      ret.maxWait = debounceMaxWait;
    }
    return ret;
  }, [debounceLeading, debounceTrailing, debounceMaxWait]);

  const changeQueryOptions = useCreation(() => {
    const handler = (options) => {
      setQueryOptions?.({ queryHash: options.queryHash, enabled: options.enabled });
    };

    if (!debounceWait) return null;

    return debounce(handler, debounceWait, debounceOptions);
  }, [debounceWait, debounceOptions]);

  useEffect(() => {
    changeQueryOptions?.(otherOptions);
  }, [otherOptions.queryHash, otherOptions.enabled]);

  return [queryOptions, setQueryOptions] as [
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    Dispatch<SetStateAction<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>>,
  ];
};
